import { motion } from 'framer-motion';
import { FLEET } from './data';

// FLEET — broker-style spec sheet, three aircraft. Distinctive: each aircraft is
// a "registration card" with monospace data table, NOT a hero image with caption.
export default function Fleet({ setPage }) {
  return (
    <div className="bg-[#0F0F11] text-[#EAF1F4]">
      <section className="pt-14 pb-10 max-w-[1500px] mx-auto px-6 lg:px-10 border-b border-[#EAF1F4]/10">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-4">⏵ Section 02 / Fleet Inventory</p>
        <h1 className="font-serif text-5xl lg:text-8xl leading-[0.92] tracking-[-0.025em] max-w-5xl">Three aircraft. <span className="italic text-[#D73B26]">All ours</span>.</h1>
        <p className="text-base text-[#EAF1F4]/55 mt-8 max-w-2xl leading-[1.7]">Below is the live registry. Tail numbers, range envelopes, hourly rates — verifiable against the FAA and EASA registers.</p>
      </section>

      {/* Spec cards — ID-card style: photo top, full data table below */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-16 space-y-12">
        {FLEET.map((f, i) => (
          <motion.div key={f.tail} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="grid lg:grid-cols-[1.3fr_1fr] gap-0 border border-[#EAF1F4]/15 bg-[#06060A] overflow-hidden">
            <div className="relative aspect-[16/10] lg:aspect-auto">
              <img src={f.img} alt={f.model} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="font-mono text-[9px] tracking-[0.3em] uppercase bg-[#D73B26] text-[#0F0F11] px-2.5 py-1">{f.cat}</span>
                <span className="font-mono text-[9px] tracking-[0.3em] uppercase bg-[#06060A]/80 backdrop-blur-sm border border-[#EAF1F4]/20 text-[#EAF1F4] px-2.5 py-1">{f.tail}</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end font-mono text-[10px] tracking-[0.25em] uppercase text-[#EAF1F4]/85">
                <span>0{i + 1} / 03</span>
                <span className="text-[#D73B26]">{f.hourly}</span>
              </div>
            </div>
            <div className="p-8 lg:p-10">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-2">/ Aircraft Type</p>
              <h2 className="font-serif text-3xl lg:text-4xl leading-tight">{f.model}</h2>
              <table className="font-mono text-[11px] mt-8 w-full">
                <tbody>
                  {[['Tail', f.tail], ['Category', f.cat], ['Range', f.range], ['Cruise', f.speed], ['Pax', f.pax], ['Hourly', f.hourly]].map(([k, v]) => (
                    <tr key={k} className="border-b border-[#EAF1F4]/10">
                      <td className="py-2.5 text-[#EAF1F4]/40 tracking-[0.2em] uppercase w-24">{k}</td>
                      <td className="py-2.5 text-[#EAF1F4]/95">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-sm text-[#EAF1F4]/55 mt-6 leading-[1.7]">{f.cabin}</p>
              <button onClick={() => setPage('quote')} data-testid={`jet-fleet-quote-${f.cat}`}
                className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase border border-[#D73B26] text-[#D73B26] px-4 py-2.5 hover:bg-[#D73B26] hover:text-[#0F0F11] transition-colors">
                Quote {f.cat.toLowerCase()} →
              </button>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Range chart — abstract — shows reach */}
      <section className="bg-[#06060A] border-y border-[#EAF1F4]/10 py-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-3">⏵ Range envelope · NM</p>
          <h2 className="font-serif text-3xl lg:text-5xl mb-10">From London, <span className="italic text-[#D73B26]">non-stop</span>.</h2>
          <div className="space-y-3 font-mono text-[11px]">
            {[
              { ac: 'Phenom 300E', val: 2010, max: 7500, dest: 'Tunis · Reykjavik · Cairo' },
              { ac: 'Falcon 7X', val: 5950, max: 7500, dest: 'New York · Mumbai · Beijing' },
              { ac: 'Gulfstream G650ER', val: 7500, max: 7500, dest: 'Tokyo · Buenos Aires · Sydney' },
            ].map(r => (
              <div key={r.ac} className="grid grid-cols-[140px_1fr_auto] gap-4 lg:gap-8 items-center">
                <span className="text-[#EAF1F4]/85">{r.ac}</span>
                <div className="h-6 bg-[#EAF1F4]/[0.04] relative overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${(r.val / r.max) * 100}%` }} viewport={{ once: true }} transition={{ duration: 1.4, ease: 'easeOut' }}
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D73B26] to-[#FFC93B] flex items-center justify-end pr-2 text-[10px] text-[#0F0F11]">
                    {r.val.toLocaleString()} NM
                  </motion.div>
                </div>
                <span className="text-[#EAF1F4]/45 text-right hidden sm:block">{r.dest}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
